/**
 * Assessment instances — one record per employee per cycle.
 *
 * Status model: no record = "not started"; a record moves
 * in_progress → submitted → reviewed. Answers live in a flat map keyed
 * `sectionId.rowId` (matrix/multiSelect/followUp) or `sectionId.questionId`
 * (free text), so autosave is a single object merge.
 *
 * localStorage map keyed by assessment id. Backend seam: a future
 * syncAssessments(token) hydrates the same cache; getters stay synchronous.
 */
import { emitGrowthChange, newId } from './events';

const STORAGE_KEY = 'peoplevue_growth_assessments';

function readLocal() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function writeLocal(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

export function getAssessments() {
  return Object.values(readLocal());
}

export function getAssessment(id) {
  return readLocal()[id] || null;
}

/** All of an employee's assessments, newest first. */
export function getAssessmentsFor(employeeId) {
  return getAssessments()
    .filter((a) => a.employeeId === employeeId)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

/** The employee's assessment for a cycle, or null (= not started). */
export function getCurrentAssessment(employeeId, cycleId) {
  return getAssessmentsFor(employeeId).find((a) => a.cycleId === cycleId) || null;
}

/** Most recent submitted/reviewed assessment BEFORE the given cycle (for deltas). */
export function getPreviousAssessment(employeeId, cycleId) {
  return (
    getAssessmentsFor(employeeId).find(
      (a) => a.cycleId !== cycleId && a.status !== 'in_progress'
    ) || null
  );
}

export function startAssessment(employeeId, template, cycleId) {
  const existing = getCurrentAssessment(employeeId, cycleId);
  if (existing && existing.templateId === template.id) return existing;
  // A mid-cycle reroute (admin moved this person to another department's
  // template) can leave an in-progress record for the OLD template under
  // this employee+cycle. Resuming it would keep the wizard writing answers
  // nobody reads, so replace it in place. Submitted/reviewed records for a
  // different template are left untouched as history — callers (MyGrowthTab)
  // only reach this branch for in-progress staleness, but guard here too so
  // this function is safe on its own.
  if (existing && existing.status !== 'in_progress') return existing;
  const map = readLocal();
  const record = {
    id: existing?.id || newId('asmt'),
    employeeId,
    templateId: template.id,
    templateVersion: template.version,
    cycleId,
    status: 'in_progress',
    answers: {},
    createdAt: existing?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  map[record.id] = record;
  writeLocal(map);
  emitGrowthChange();
  return record;
}

/** Autosave: merge a partial answers object into the draft. */
export function saveAnswers(assessmentId, partialAnswers) {
  const map = readLocal();
  const record = map[assessmentId];
  if (!record) return null;
  map[assessmentId] = {
    ...record,
    answers: { ...record.answers, ...partialAnswers },
    updatedAt: new Date().toISOString(),
  };
  writeLocal(map);
  emitGrowthChange();
  return map[assessmentId];
}

export function submitAssessment(assessmentId) {
  return transition(assessmentId, (r) => ({
    ...r,
    status: 'submitted',
    submittedAt: new Date().toISOString(),
  }));
}

export function markReviewed(assessmentId, reviewerId, managerNote = '') {
  return transition(assessmentId, (r) => ({
    ...r,
    status: 'reviewed',
    reviewedAt: new Date().toISOString(),
    reviewedBy: reviewerId,
    managerNote,
  }));
}

function transition(assessmentId, fn) {
  const map = readLocal();
  const record = map[assessmentId];
  if (!record) return null;
  map[assessmentId] = { ...fn(record), updatedAt: new Date().toISOString() };
  writeLocal(map);
  emitGrowthChange();
  return map[assessmentId];
}

/** Display status for any employee: 'not_started' | 'in_progress' | 'submitted' | 'reviewed'. */
export function getStatusFor(employeeId, cycleId) {
  return getCurrentAssessment(employeeId, cycleId)?.status || 'not_started';
}
